import { useEffect, useRef, useCallback, useContext } from 'react'
import { AuthContext } from 'src/context/AuthContext'

const MAX_RETRIES = 5

const useWebSocket = url => {
  const {
    socketData,
    setSocketData,
    isConnected,
    setIsConnected,
    notificationMessage,
    openPopUp,
    setOpenPopUp,
    isUserLoggedIn,
    loginMessagePending,
    setLoginMessagePending,
    fetchNotificationContent,
    markAsRead,
    markAllAsRead
  } = useContext(AuthContext)

  const ws = useRef(null)
  const retries = useRef(0)
  const shouldReconnect = useRef(false)
  const lastAutoFetchId = useRef(null)
  const fetchNotificationContentRef = useRef(null)
  const loginMessagePendingRef = useRef(loginMessagePending)

  useEffect(() => {
    loginMessagePendingRef.current = loginMessagePending
  }, [loginMessagePending])

  useEffect(() => {
    fetchNotificationContentRef.current = fetchNotificationContent
  }, [fetchNotificationContent])

  const connectWebSocket = useCallback(() => {
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return
    }

    ws.current = new WebSocket(url)

    ws.current.onopen = () => {
      retries.current = 0
      setIsConnected(true)

      if (loginMessagePendingRef.current) {
        ws.current.send('login')
        setLoginMessagePending(false)
      } else {
        ws.current.send('refresh')
      }
    }

    ws.current.onmessage = event => {
      try {
        const data = JSON.parse(event.data)

        const isUnread = notification => {
          return !notification.messageReadStatus || notification.messageReadStatus === 'unread'
        }

        if (Array.isArray(data)) {
          if (data.length !== 1) {
            setSocketData(data)
          } else {
            const newNotification = data[0]

            setSocketData(prevData => {
              const existingIds = prevData ? prevData.map(n => n.notificationId) : []

              if (existingIds.includes(newNotification.notificationId)) {
                return prevData
              }

              return [newNotification, ...(prevData || [])]
            })

            if (isUnread(newNotification) && lastAutoFetchId.current !== newNotification.notificationId) {
              lastAutoFetchId.current = newNotification.notificationId

              fetchNotificationContentRef.current?.(newNotification.notificationId).then(() => {
                setTimeout(() => {
                  if (ws.current?.readyState === WebSocket.OPEN) {
                    ws.current.send('refresh')
                  }
                }, 500)
              })
            }
          }
        }
      } catch {
      }
    }

    ws.current.onclose = () => {
      setIsConnected(false)

      if (!shouldReconnect.current) return

      if (retries.current >= MAX_RETRIES) {
        return
      }

      retries.current += 1
      const delay = Math.min(1000 * 2 ** retries.current, 30000)

      setTimeout(connectWebSocket, delay)
    }

    ws.current.onerror = () => {
    }
  }, [url, setSocketData, setIsConnected, setLoginMessagePending])

  useEffect(() => {
    if (!isUserLoggedIn) {
      shouldReconnect.current = false
      lastAutoFetchId.current = null
      ws.current?.close()
      return
    }

    shouldReconnect.current = true
    connectWebSocket()

    return () => {
      shouldReconnect.current = false
      lastAutoFetchId.current = null
      ws.current?.close()
    }
  }, [isUserLoggedIn, connectWebSocket])

  const sendMessage = useCallback(message => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(typeof message === 'string' ? message : JSON.stringify(message))
    } else {
    }
  }, [])

  return {
    socketData,
    sendMessage,
    isConnected,
    notificationMessage,
    openPopUp,
    setOpenPopUp,
    fetchNotificationContent,
    markAsRead,
    markAllAsRead
  }
}

export default useWebSocket
